import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { DaInput, DaSelect, InternalGrid, Box } from '@paygreen/paygreen-ui';
import { FormControlWrapper } from '../../../components';
import { IsMobile } from '../../../utils/tools';
import { billingAddressPatterns, countryOptions } from '../shared';

const UserCompanyBillingAddressForm = ({
    t,
    register,
    errors,
    setValue,
    onSubmit,
    children,
    defaultValues,
}) => {
    const transversalInputProps = {
        errors: errors,
        i18labelBase: 'user.company',
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValue(name, value);
    };

    const handleCountryChange = (event) => {
        setValue('country', event.target.value)
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <InternalGrid
                displayType="grid"
                gridGap="md"
                alignItems="stretch"
                justifyItems="stretch"
                gridTemplateColumns="1fr"
            >
                {billingAddressPatterns
                    .filter(
                        (element) =>
                            element.name === 'addressLine1' ||
                            element.name === 'addressLine2',
                    )
                    .map((element) => (
                        <FormControlWrapper
                            key={element.name}
                            inputName={element.name}
                            isRequired={element.name === 'addressLine1'}
                            marginBottom={
                                IsMobile() && element.name === 'addressLine2'
                                    ? 'sm'
                                    : null
                            }
                            {...transversalInputProps}
                        >
                            <DaInput
                                onChange={handleChange}
                                name={element.name}
                                id={element.name}
                                type={element.type}
                                defaultValue={defaultValues[element.name]}
                                inputRef={register({
                                    required:
                                        element.name === 'addressLine1'
                                            ? t('form.error.required')
                                            : null,
                                    pattern: {
                                        value: element.pattern,
                                        message: t(
                                            `form.error.${element.name}`,
                                        ),
                                    },
                                })}
                                blockWidth="lg"
                            />
                        </FormControlWrapper>
                    ))}
            </InternalGrid>

            <Box marginTop={IsMobile() ? 'sm' : 'md'}>
                <InternalGrid
                    displayType="grid"
                    gridGap="md"
                    alignItems="stretch"
                    justifyItems="stretch"
                    gridTemplateColumns="1fr"
                >
                    {billingAddressPatterns
                        .filter(
                            (elem) =>
                                elem.name === 'city' || elem.name === 'zipCode',
                        )
                        .map((element) => (
                            <FormControlWrapper
                                key={element.name}
                                inputName={element.name}
                                isRequired={true}
                                {...transversalInputProps}
                            >
                                <DaInput
                                    onChange={handleChange}
                                    name={element.name}
                                    id={element.name}
                                    type={element.type}
                                    defaultValue={defaultValues[element.name]}
                                    inputRef={register({
                                        required: t('form.error.required'),
                                        pattern: {
                                            value: element.pattern,
                                            message: t(
                                                `form.error.${element.name}`,
                                            ),
                                        },
                                    })}
                                    blockWidth="md"
                                />
                            </FormControlWrapper>
                        ))}
                </InternalGrid>
            </Box>
            <InternalGrid
                displayType="grid"
                gridGap="md"
                alignItems="stretch"
                justifyItems="stretch"
                gridTemplateColumns={IsMobile() ? '1fr' : '1fr 1fr'}
            >
                {billingAddressPatterns
                    .filter((elem) => elem.name === 'region')
                    .map((element) => (
                        <FormControlWrapper
                            key={element.name}
                            inputName={element.name}
                            marginTop={IsMobile() ? null : 'md'}
                            {...transversalInputProps}
                        >
                            <DaInput
                                onChange={handleChange}
                                name={element.name}
                                id={element.name}
                                type={element.type}
                                defaultValue={defaultValues[element.name]}
                                inputRef={register({
                                    pattern: {
                                        value: element.pattern,
                                        message: t(
                                            `form.error.${element.name}`,
                                        ),
                                    },
                                })}
                                blockWidth="md"
                            />
                        </FormControlWrapper>
                    ))}

                <FormControlWrapper
                    hasStaticWidth={IsMobile()}
                    inputName="country"
                    isRequired={true}
                    marginTop={IsMobile() ? 'sm' : 'md'}
                    {...transversalInputProps}
                >
                    <DaSelect
                        hasStaticWidth={IsMobile()}
                        blockWidth="sm"
                        name="country"
                        id="country"
                        inputRef={register}
                        options={countryOptions(t)}
                        onChange={handleCountryChange}
                        defaultValue={defaultValues.country}
                    />
                </FormControlWrapper>
            </InternalGrid>

            {children}
        </form>
    );
};

UserCompanyBillingAddressForm.propTypes = {
    setValue: PropTypes.func.isRequired,
    register: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    /**
     * errors passed by react-hook-form for each field (necessary if you want errors management)
     */
    errors: PropTypes.object,
    defaultValues: PropTypes.object,
};

UserCompanyBillingAddressForm.defaultProps = {
    errors: {},
};

export default withTranslation(
    'common',
    'countries',
)(UserCompanyBillingAddressForm);
